import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Container, Typography, Stack, Paper, Chip, IconButton, Tooltip,
  CircularProgress, Divider, TextField, Button
} from '@mui/material';
import {
  ArrowBack, BarChart, TrendingUp, AccessTime,
  Phone, SmartToy, Person
} from '@mui/icons-material';
import { apiRequest } from '../../utils/api';

const shortcuts = [
  { label: 'Hoy', getRange: () => { const d = new Date().toISOString().split('T')[0]; return { from: d, to: d }; } },
  { label: 'Esta semana', getRange: () => { const now = new Date(); const mon = new Date(now); mon.setDate(now.getDate() - now.getDay() + 1); return { from: mon.toISOString().split('T')[0], to: now.toISOString().split('T')[0] }; } },
  { label: 'Este mes', getRange: () => { const now = new Date(); return { from: `${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}-01`, to: now.toISOString().split('T')[0] }; } },
  { label: 'Este año', getRange: () => { const now = new Date(); return { from: `${now.getFullYear()}-01-01`, to: now.toISOString().split('T')[0] }; } },
];

export default function Reports() {
  const navigate = useNavigate();
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 30); return d.toISOString().split('T')[0]; });
  const [to, setTo] = useState(() => new Date().toISOString().split('T')[0]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const data = await apiRequest(`/admin/reports?from=${from}&to=${to}`);
      setReport(data);
    } catch { setReport(null); }
    setLoading(false);
  }, [from, to]);

  const applyShortcut = (s) => {
    const { from: f, to: t } = s.getRange();
    setFrom(f); setTo(t);
  };

  const formatDuration = (secs) => {
    if (!secs) return '—';
    if (secs < 60) return `${Math.round(secs)}s`;
    return `${Math.round(secs / 60)} min`;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#F8FAFC' }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid', borderColor: 'divider', position: 'sticky', top: 0, zIndex: 30 }}>
        <Container maxWidth="xl">
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ py: 1.5 }}>
            <Stack direction="row" alignItems="center" spacing={2}>
              <IconButton onClick={() => navigate('/admin/control-room')} size="small"><ArrowBack /></IconButton>
              <Typography variant="h5" sx={{ fontWeight: 800, background: 'linear-gradient(135deg, #2563EB, #7C3AED)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>Vincula</Typography>
              <Chip label="Reportes" size="small" variant="outlined" icon={<BarChart sx={{ fontSize: 14 }} />} />
            </Stack>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* Period selector */}
        <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <Stack direction="row" spacing={1}>
              {shortcuts.map(s => (
                <Chip key={s.label} label={s.label} size="small" clickable onClick={() => applyShortcut(s)}
                  sx={{ '&:hover': { bgcolor: '#EEF2FF' } }} />
              ))}
            </Stack>
            <TextField size="small" type="date" label="Desde" value={from} onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            <TextField size="small" type="date" label="Hasta" value={to} onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
            <Button variant="contained" onClick={fetchReport} disabled={loading}
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <TrendingUp />}
              sx={{ background: 'linear-gradient(135deg, #2563EB, #7C3AED)', textTransform: 'none', fontWeight: 600, borderRadius: 2 }}>
              Generar
            </Button>
          </Stack>
        </Paper>

        {report && (
          <>
            {/* Metric cards */}
            <Stack direction="row" spacing={2} sx={{ mb: 3, overflowX: 'auto', flexWrap: 'nowrap' }}>
              {[
                { label: 'Total llamadas', value: report.total_calls, icon: <Phone />, color: '#475569' },
                { label: 'Completadas', value: report.completed_calls, icon: <Phone />, color: '#10B981' },
                { label: 'Fallidas', value: report.failed_calls, icon: <Phone />, color: '#EF4444' },
                { label: 'Con IA', value: report.ai_calls, icon: <SmartToy />, color: '#7C3AED' },
                { label: 'Con humano', value: report.human_calls, icon: <Person />, color: '#2563EB' },
                { label: 'Duración total', value: formatDuration(report.total_duration_seconds), icon: <AccessTime />, color: '#475569' },
                { label: 'Promedio', value: formatDuration(report.avg_duration_seconds), icon: <AccessTime />, color: '#2563EB' },
                { label: 'Mediana', value: formatDuration(report.median_duration_seconds), icon: <AccessTime />, color: '#7C3AED' },
              ].map(s => (
                <Paper key={s.label} sx={{ p: 2, minWidth: 130, borderRadius: 3, flex: '1 0 auto' }}>
                  <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                    <Box sx={{ color: s.color, '& svg': { fontSize: 16 } }}>{s.icon}</Box>
                    <Typography variant="caption" color="text.secondary">{s.label}</Typography>
                  </Stack>
                  <Typography variant="h5" fontWeight={800} sx={{ color: s.color }}>{s.value}</Typography>
                </Paper>
              ))}
            </Stack>

            {/* Extra stats */}
            <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
              <Paper sx={{ p: 2, borderRadius: 3, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Recordatorios creados</Typography>
                <Typography variant="h4" fontWeight={800} color="#F59E0B">{report.reminders_created}</Typography>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 3, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Recordatorios enviados</Typography>
                <Typography variant="h4" fontWeight={800} color="#10B981">{report.reminders_sent}</Typography>
              </Paper>
              <Paper sx={{ p: 2, borderRadius: 3, flex: 1 }}>
                <Typography variant="body2" color="text.secondary">Alertas proactivas</Typography>
                <Typography variant="h4" fontWeight={800} color="#EF4444">{report.alerts_generated}</Typography>
              </Paper>
            </Stack>

            {/* Top patients */}
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Box sx={{ p: 2, bgcolor: '#F8FAFC', borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="h6" fontWeight={700}>Llamadas por paciente</Typography>
              </Box>
              {report.top_patients?.length === 0 ? (
                <Box sx={{ p: 3, textAlign: 'center' }}><Typography color="text.secondary">Sin datos en este período</Typography></Box>
              ) : (
                report.top_patients?.map((p, idx) => (
                  <React.Fragment key={p.patient_id}>
                    {idx > 0 && <Divider />}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2, py: 1.5 }}>
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Chip label={idx + 1} size="small" sx={{ fontWeight: 700, width: 28, height: 28, bgcolor: idx < 3 ? '#EEF2FF' : '#F8FAFC', color: idx < 3 ? '#4F46E5' : '#475569' }} />
                        <Typography variant="body2" fontWeight={600}>{p.patient_name}</Typography>
                      </Stack>
                      <Stack direction="row" spacing={2}>
                        <Typography variant="body2" color="text.secondary">{p.call_count} llamadas</Typography>
                        <Typography variant="body2" color="text.secondary">{formatDuration(p.total_duration)} total</Typography>
                        <Typography variant="body2" color="text.secondary">{formatDuration(p.avg_duration)} prom</Typography>
                      </Stack>
                    </Stack>
                  </React.Fragment>
                ))
              )}
            </Paper>
          </>
        )}

        {!report && !loading && (
          <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
            <BarChart sx={{ fontSize: 48, color: '#CBD5E1', mb: 1 }} />
            <Typography color="text.secondary">Seleccioná un período y hacé click en "Generar"</Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
